import { useBundle } from "@amzn/react-arb-tools";
import { useEffect, useMemo, useRef, useState } from "react";

export interface LocalizedProps {
  id: string;
  namespace: string;
  fallbackFactory?: (id: string) => string;
  args?: Record<string, string | number>;
}

/**
 * https://18.react.dev/reference/react/Suspense
 * Suspended localization component. Used in a React <Suspense> block to work with XB suspense pattern.
 * React suspense normally works within the paradigm of the react `use` api. However, we cannot easily upgrade to
 * React 19 to get access to the `use` api. This component is a workaround which allows us to use the suspense pattern
 * by throwing a promise which is caught by the suspense block. We choose this approach because there is no library in
 * React 18 which we could find to implement this functionality for us.
 * https://tasks.awstcprojects.com/browse/PLATFORM-7027
 */
export const Localized: ({ id, namespace, args, fallbackFactory }: LocalizedProps) => React.ReactNode = ({
  id,
  namespace,
  fallbackFactory = () => id,
  args,
}: LocalizedProps) => {
  const [bundle, stringsLoading] = useBundle(namespace);
  const promise = useRef<Promise<void>>();
  useEffect(() => {
    if (stringsLoading) {
      promise.current = new Promise(() => {});
    }
  }, [stringsLoading]);
  if (stringsLoading && promise.current) {
    throw promise.current;
  }
  const translation = useMemo(() => {
    try {
      return bundle?.formatMessage(id, args) ?? fallbackFactory(id);
    } catch (e) {
      return fallbackFactory(id);
    }
  }, [bundle, id, args, fallbackFactory]);
  return <>{translation}</>;
};
